import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/common'
import Logo from '@/components/common/Logo'
import { toast } from 'react-toastify'
import { API_BASE_URL } from '@/constants'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [email, setEmail] = useState('')
  const [success, setSuccess] = useState(false)

  // Verify token on mount
  useEffect(() => {
    if (token) {
      verifyToken()
    } else {
      setVerifying(false)
      toast.error('ลิงก์ไม่ถูกต้อง')
    }
  }, [token])

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-reset-token/${token}`)
      const data = await response.json()

      if (response.ok && data.valid) {
        setTokenValid(true)
        setEmail(data.email)
      } else {
        setTokenValid(false)
        toast.error(data.message || 'ลิงก์ไม่ถูกต้องหรือหมดอายุแล้ว')
      }
    } catch (error) {
      console.error('Token verification error:', error)
      setTokenValid(false)
      toast.error('ไม่สามารถตรวจสอบลิงก์ได้')
    } finally {
      setVerifying(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      toast.error('รหัสผ่านไม่ตรงกัน')
      return
    }

    // Validate password length
    if (newPassword.length < 6) {
      toast.error('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร')
      return
    }

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess(true)
        toast.success('เปลี่ยนรหัสผ่านสำเร็จ!')

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      } else {
        toast.error(data.message || 'ไม่สามารถเปลี่ยนรหัสผ่านได้')
      }
    } catch (error) {
      console.error('Reset password error:', error)
      toast.error('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้')
    } finally {
      setLoading(false)
    }
  }

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800 mx-auto mb-4"></div>
          <p className="text-text-secondary">กำลังตรวจสอบลิงก์...</p>
        </div>
      </div>
    )
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-error-light rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-10 h-10 text-error" />
            </div>

            <h1 className="text-2xl font-bold text-text-primary mb-2">
              ลิงก์ไม่ถูกต้องหรือหมดอายุ
            </h1>

            <p className="text-text-secondary mb-6">
              ลิงก์รีเซ็ตรหัสผ่านนี้อาจจะ:
            </p>

            <ul className="text-left text-sm text-text-secondary mb-6 space-y-2">
              <li>• ถูกใช้งานไปแล้ว</li>
              <li>• หมดอายุ (เกิน 24 ชั่วโมง)</li>
              <li>• ไม่ถูกต้อง</li>
            </ul>

            <Button
              variant="primary"
              onClick={() => navigate('/forgot-password')}
              className="w-full mb-3"
            >
              ขอลิงก์ใหม่
            </Button>

            <Button
              variant="secondary"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              กลับไปหน้าเข้าสู่ระบบ
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-success-light rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>

            <h1 className="text-2xl font-bold text-text-primary mb-2">
              เปลี่ยนรหัสผ่านสำเร็จ!
            </h1>

            <p className="text-text-secondary mb-6">
              รหัสผ่านของคุณถูกเปลี่ยนเรียบร้อยแล้ว
              <br />
              กำลังนำคุณไปยังหน้าเข้าสู่ระบบ...
            </p>

            <Button
              variant="primary"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              เข้าสู่ระบบ
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Logo size="lg" variant="full" showText={true} />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-text-primary mb-2">ตั้งรหัสผ่านใหม่</h1>
          <p className="text-text-secondary">
            สำหรับบัญชี: <strong>{email}</strong>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password Input */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-text-primary mb-2">
              รหัสผ่านใหม่
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
              <input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full pl-10 pr-12 py-3 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent"
                placeholder="อย่างน้อย 6 ตัวอักษร"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password Input */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-2">
              ยืนยันรหัสผ่านใหม่
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full pl-10 pr-12 py-3 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent"
                placeholder="ยืนยันรหัสผ่าน"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Password strength indicator */}
          {newPassword && (
            <div className="text-xs text-text-secondary">
              <p className="mb-1">ความแข็งแรงของรหัสผ่าน:</p>
              <div className="flex gap-1">
                <div className={`h-1 flex-1 rounded ${newPassword.length >= 6 ? 'bg-success' : 'bg-border-medium'}`}></div>
                <div className={`h-1 flex-1 rounded ${newPassword.length >= 8 ? 'bg-success' : 'bg-border-medium'}`}></div>
                <div className={`h-1 flex-1 rounded ${newPassword.length >= 10 && /[A-Z]/.test(newPassword) ? 'bg-success' : 'bg-border-medium'}`}></div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="w-full"
          >
            {loading ? 'กำลังบันทึก...' : 'บันทึกรหัสผ่านใหม่'}
          </Button>
        </form>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-background-secondary rounded-lg">
          <p className="text-xs text-text-secondary text-center">
            ต้องการความช่วยเหลือ?{' '}
            <a
              href="mailto:aisenovate@gmail.com"
              className="text-primary-800 hover:text-primary-900 font-medium"
            >
              ติดต่อเรา
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
